import { Fragment, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { ChevronDown, Search, CheckCircle2, Clock3, Hourglass, Building2, Mail, Pencil, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { fmtMoney, fmtDate, statusLabel, statusClass, unitLabel } from "../lib/format";

const totalsStr = (t) => Object.entries(t || {}).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—";

const KPI_META = {
  PAID: { label: "Ödenen", icon: CheckCircle2, cls: "border-[#1C4E35]/40 bg-[#1C4E35]/5", text: "text-[#1C4E35]" },
  PAYING: { label: "Ödeme Başlatılan", icon: Clock3, cls: "border-sky-300 bg-sky-50/50", text: "text-sky-700" },
  PENDING: { label: "Ödenmeyen", icon: Hourglass, cls: "border-amber-300 bg-amber-50/50", text: "text-amber-700" },
};

const SPACE_TYPES = [["", "Tümü"], ["delegation", "Ofisler"], ["pavilion", "Pavilyonlar"], ["stand", "Stantlar"]];

export default function FinancePage() {
  const { user } = useAuth();
  const [spaceType, setSpaceType] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [contacts, setContacts] = useState({});
  const [contactDialog, setContactDialog] = useState(null);
  const [contactEmail, setContactEmail] = useState("");
  const [sending, setSending] = useState({});
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/finance/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    setLoading(true);
    api.get("/finance/summary", { params: { space_type: spaceType } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [spaceType, user]);

  const spaces = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.spaces;
    return data.spaces.filter((s) => (s.organisation || "").toLowerCase().includes(q) || (s.space || "").toLowerCase().includes(q));
  }, [data, search]);

  const pendingOrgs = useMemo(() => {
    const set = new Set();
    (data?.spaces || []).forEach((s) => { if (s.statusTotals?.PENDING) set.add(s.organisation); });
    return [...set];
  }, [data]);
  const orgsWithEmail = pendingOrgs.filter((o) => contacts[o]);

  const saveContact = async () => {
    try {
      await api.post("/finance/contacts", { organisation: contactDialog.organisation, email: contactEmail });
      setContacts((c) => {
        const n = { ...c };
        if (contactEmail.trim()) n[contactDialog.organisation] = contactEmail.trim();
        else delete n[contactDialog.organisation];
        return n;
      });
      toast.success("İletişim e-postası kaydedildi");
      setContactDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const sendReminder = async (org) => {
    if (!contacts[org]) {
      setContactDialog({ organisation: org, thenSend: true });
      setContactEmail("");
      return;
    }
    setSending((s) => ({ ...s, [org]: true }));
    try {
      const r = await api.post("/finance/remind", { organisation: org });
      toast.success(`Hatırlatma gönderildi: ${r.data.to} (${r.data.orders} sipariş)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gönderilemedi");
    } finally {
      setSending((s) => ({ ...s, [org]: false }));
    }
  };

  const sendAll = async () => {
    setBulkConfirm(false);
    setBulkSending(true);
    try {
      const r = await api.post("/finance/remind-all");
      const parts = [`${r.data.sent} müşteriye gönderildi`];
      if (r.data.skipped.length) parts.push(`${r.data.skipped.length} e-postasız atlandı`);
      if (r.data.failed.length) parts.push(`${r.data.failed.length} başarısız`);
      toast.success(parts.join(" · "), { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu gönderim başarısız");
    } finally {
      setBulkSending(false);
    }
  };

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <main className="mx-auto max-w-7xl space-y-4 px-4 py-6 md:px-6" data-testid="finance-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden border border-stone-300" data-testid="finance-space-type-filter">
          {SPACE_TYPES.map(([val, label]) => (
            <button
              key={val}
              onClick={() => setSpaceType(val)}
              data-testid={`finance-space-type-${val || "all"}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${spaceType === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Müşteri veya alan ara..."
            data-testid="finance-search-input"
            className="h-8 w-56 rounded-sm border-stone-300 pl-8 text-xs"
          />
        </div>
        {data && <p className="text-xs text-stone-500" data-testid="finance-count">{data.orderCount} sipariş · {spaces.length} alan</p>}
        <Button
          variant="outline"
          size="sm"
          disabled={bulkSending || orgsWithEmail.length === 0}
          onClick={() => setBulkConfirm(true)}
          data-testid="finance-remind-all-button"
          className="ml-auto h-8 rounded-sm border-amber-400 bg-amber-50 text-xs text-amber-800 hover:bg-amber-100 active:scale-95"
        >
          {bulkSending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Mail size={13} className="mr-1.5" />}
          Tüm Ödenmeyenlere Gönder ({orgsWithEmail.length}/{pendingOrgs.length})
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="finance-kpi-cards">
        {(() => {
          const kpis = data?.kpis || ["PAID", "PAYING", "PENDING"].map((s) => ({ status: s, count: 0, totals: {} }));
          const sumOf = (t) => Object.values(t || {}).reduce((a, b) => a + b, 0);
          const grand = kpis.reduce((a, k) => a + sumOf(k.totals), 0);
          return kpis.map((k) => {
            const m = KPI_META[k.status];
            const Icon = m.icon;
            const pct = grand ? (sumOf(k.totals) / grand) * 100 : 0;
            const pctStr = `%${pct.toFixed(1).replace(".", ",").replace(",0", "")}`;
            return (
              <div key={k.status} className={`fade-up border p-4 ${m.cls}`} data-testid={`finance-kpi-${k.status}`}>
                <div className="flex items-center justify-between">
                  <p className={`text-[11px] font-bold uppercase tracking-[0.18em] ${m.text}`}>{m.label}</p>
                  <Icon size={16} className={m.text} />
                </div>
                <p className={`mt-1 font-heading text-4xl font-black tracking-tight sm:text-5xl ${m.text}`} data-testid={`finance-kpi-pct-${k.status}`}>
                  {loading ? "…" : pctStr}
                </p>
                <p className="mt-1 font-heading text-lg font-bold tracking-tight text-[#0A0D0B]" data-testid={`finance-kpi-total-${k.status}`}>
                  {loading ? "…" : totalsStr(k.totals)}
                </p>
                <p className="mt-0.5 text-[11px] text-stone-500">{k.count} sipariş</p>
              </div>
            );
          });
        })()}
      </div>

      <div className="border border-stone-200 bg-white">
        {loading ? (
          <div className="flex h-40 items-center justify-center" data-testid="finance-loading">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
          </div>
        ) : spaces.length === 0 ? (
          <p className="p-8 text-center text-sm text-stone-500" data-testid="finance-empty">Kayıt bulunamadı.</p>
        ) : (
          <Table data-testid="finance-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Alan / Müşteri</TableHead>
                <TableHead className="text-xs">Tip</TableHead>
                <TableHead className="text-xs">Ödeme Durumu</TableHead>
                <TableHead className="text-right text-xs">Sipariş</TableHead>
                <TableHead className="text-right text-xs">Toplam Tutar</TableHead>
                <TableHead className="text-right text-xs">Hatırlatma</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {spaces.map((s) => {
                const key = `${s.organisation}|${s.space}`;
                const isOpen = !!open[key];
                return (
                  <Fragment key={key}>
                    <TableRow
                      onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                      className="cursor-pointer select-none transition-colors hover:bg-stone-50"
                      data-testid={`finance-row-${s.space.replace(/\s+/g, "_")}`}
                    >
                      <TableCell className="py-2.5">
                        <span className="flex items-start gap-2">
                          <ChevronDown size={14} className={`mt-1 shrink-0 text-stone-400 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
                          <span>
                            <p className="text-sm font-medium">{s.space}</p>
                            <p className="flex items-center gap-1 text-[11px] text-stone-500">
                              <Building2 size={11} /> {s.organisation}
                              {s.zone && <span className="text-stone-400"> · {s.zone}</span>}
                            </p>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className="border border-stone-300 bg-stone-50 px-1.5 py-0.5 text-[10px] font-medium text-stone-600">{s.type}</span>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className="flex flex-wrap gap-1">
                          {["PAID", "PAYING", "PENDING"].filter((st) => s.statusTotals[st]).map((st) => (
                            <span key={st} className={`border px-1.5 py-0.5 text-[10px] font-medium ${statusClass(st)}`} data-testid={`finance-status-${s.space.replace(/\s+/g, "_")}-${st}`}>
                              {statusLabel(st)}: {totalsStr(s.statusTotals[st])}
                            </span>
                          ))}
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-sm">{s.orderCount}</TableCell>
                      <TableCell className="py-2.5 text-right text-sm font-semibold" data-testid={`finance-total-${s.space.replace(/\s+/g, "_")}`}>
                        {totalsStr(s.totals)}
                      </TableCell>
                      <TableCell className="py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <span className="flex items-center justify-end gap-1">
                          {contacts[s.organisation] && (
                            <span className="hidden max-w-[140px] truncate text-[10px] text-stone-400 lg:inline" title={contacts[s.organisation]}>
                              {contacts[s.organisation]}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setContactDialog({ organisation: s.organisation }); setContactEmail(contacts[s.organisation] || ""); }}
                            title="İletişim e-postasını düzenle"
                            data-testid={`finance-contact-edit-${s.space.replace(/\s+/g, "_")}`}
                            className="h-7 w-7 rounded-sm border-stone-300 bg-white p-0 hover:bg-stone-100 active:scale-95"
                          >
                            <Pencil size={12} />
                          </Button>
                          {s.statusTotals.PENDING && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!!sending[s.organisation]}
                              onClick={() => sendReminder(s.organisation)}
                              title={contacts[s.organisation] ? `Ödeme hatırlatması gönder: ${contacts[s.organisation]}` : "Önce e-posta tanımlayın"}
                              data-testid={`finance-remind-${s.space.replace(/\s+/g, "_")}`}
                              className="h-7 w-7 rounded-sm border-amber-400 bg-amber-50 p-0 text-amber-700 hover:bg-amber-100 active:scale-95"
                            >
                              {sending[s.organisation] ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            </Button>
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow data-testid={`finance-lines-${s.space.replace(/\s+/g, "_")}`}>
                        <TableCell colSpan={6} className="bg-stone-50/70 p-0">
                          <div className="divide-y divide-stone-100 px-6 py-2">
                            {s.lines.map((l, i) => (
                              <div key={`${l.ref}-${l.sku}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-xs">
                                <span className="min-w-0 flex-1">
                                  <span className="font-medium text-stone-800">{l.name}</span>
                                  {l.variant && <span className="ml-1 text-stone-500">({l.variant})</span>}
                                  <span className="ml-1.5 text-[10px] text-stone-400">{l.sku} · {l.ref}</span>
                                </span>
                                <span className="text-stone-500">{l.qty} {unitLabel(l.unit)}</span>
                                <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${statusClass(l.status)}`}>{statusLabel(l.status)}</span>
                                <span className="text-stone-400">{fmtDate(l.placedAt)}</span>
                                <span className="min-w-[4.5rem] text-right font-semibold text-stone-800">{fmtMoney(l.lineTotal, l.currency)}</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!contactDialog} onOpenChange={(v) => !v && setContactDialog(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-md rounded-sm bg-white" data-testid="finance-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">İletişim E-postası</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">{contactDialog?.organisation}</p>
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="ornek@kurum.org"
            data-testid="finance-contact-email-input"
            className="rounded-sm border-stone-300"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setContactDialog(null)} className="rounded-sm text-xs">Vazgeç</Button>
            <Button size="sm" onClick={saveContact} data-testid="finance-contact-save" className="rounded-sm bg-[#1C4E35] text-xs text-white hover:bg-[#163D2A] active:scale-95">
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent className="rounded-sm border-stone-200" data-testid="finance-bulk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-base font-bold tracking-tight">Toplu Ödeme Hatırlatması</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-stone-600">
              Ödenmemiş siparişi olan {pendingOrgs.length} müşteriden e-postası kayıtlı {orgsWithEmail.length} müşteriye İngilizce ödeme hatırlatma maili gönderilecek.
              {pendingOrgs.length - orgsWithEmail.length > 0 && ` E-postası tanımlı olmayan ${pendingOrgs.length - orgsWithEmail.length} müşteri atlanacak.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-stone-300" data-testid="finance-bulk-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={sendAll} data-testid="finance-bulk-ok" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#163D2A] active:scale-95">
              Evet, gönder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
